import { useUpdateScope, useUploadJournalReport } from "../../api/hooks";
import { formatDateReadable, type Scope } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Card } from "../../components/Card";
import { t } from "../../i18n/t";

/**
 * Manual Xero Journal Report upload. Shown ONLY when the source says the connection lacks the journals scope
 * (`journals_scope_missing === true`); once Xero grants the scope the card disappears on its own and the API is
 * used. "Automatic" is today's behaviour (the gap is disclosed and acknowledged); "Manual upload" shows the upload
 * control with a one-line hint on where to export the report, then the outcome: N journals imported, M already
 * covered, K needing review. The choice is a scope key, so it is frozen at sign-off like every other.
 */
export function JournalsChoice({
  migrationId,
  scope,
  frozen,
  uploadOpen = !frozen,
  conversionDate = null,
  today = new Date(),
}: {
  migrationId: string;
  scope: Scope | undefined;
  frozen: boolean;
  /** The report may be uploaded or replaced: before sign-off, and (late) until conversion sign-off. */
  uploadOpen?: boolean;
  /** The migration's own conversion date (ISO): the first day the exported report must cover. */
  conversionDate?: string | null;
  /** Injectable for tests; the last day the report must cover is today. */
  today?: Date;
}): JSX.Element | null {
  const update = useUpdateScope(migrationId);
  const upload = useUploadJournalReport(migrationId);
  if (scope === undefined || scope.journals_scope_missing !== true) return null;

  const mode = scope.journals_mode ?? "automatic";
  const report = scope.journal_report ?? null;
  const busy = update.isPending || upload.isPending;
  const uploadError = upload.error instanceof Error ? upload.error.message : null;
  const findings = report?.findings ?? [];
  const counted = report !== null && report.imported !== null && report.imported !== undefined;

  return (
    <Card title={t("scope.journals_title")}>
      <div className="card__body" data-testid="journals-choice">
        <p>{t("scope.journals_intro")}</p>
        <fieldset>
          <legend className="visually-hidden">{t("scope.journals_mode_legend")}</legend>
          <label className="chk">
            <input
              type="radio"
              name="journals_mode"
              value="automatic"
              checked={mode === "automatic"}
              disabled={frozen || busy}
              onChange={() => update.mutate({ journals_mode: "automatic" })}
            />
            <span>
              <span className="chk__label">{t("scope.journals_mode_automatic")}</span>
              <span className="chk__sub">{t("scope.journals_mode_automatic_sub")}</span>
            </span>
          </label>
          <label className="chk">
            <input
              type="radio"
              name="journals_mode"
              value="manual_upload"
              checked={mode === "manual_upload"}
              disabled={frozen || busy}
              onChange={() => update.mutate({ journals_mode: "manual_upload" })}
            />
            <span>
              <span className="chk__label">{t("scope.journals_mode_manual")}</span>
              <span className="chk__sub">{t("scope.journals_mode_manual_sub")}</span>
            </span>
          </label>
        </fieldset>
        {frozen ? <p>{t("scope.journals_locked")}</p> : null}
        {mode === "manual_upload" ? (
          <div data-testid="journals-upload">
            {conversionDate !== null && conversionDate !== "" ? (
              <p data-testid="journals-range">
                <strong>
                  {t("scope.journals_export_range", {
                    from: formatDateReadable(conversionDate),
                    today: formatDateReadable(utcIsoDate(today)),
                  })}
                </strong>
              </p>
            ) : null}
            <p className="chk__sub">{t("scope.journals_export_why")}</p>
            <p className="chk__sub">{t("scope.journals_export_hint")}</p>
            <p className="chk__sub" data-testid="journals-columns">
              {t("scope.journals_export_columns")}
            </p>
            {uploadOpen ? (
              <label className="field">
                <span className="field__label">{t("scope.journals_upload_label")}</span>
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={busy}
                  data-testid="journals-file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file !== undefined) upload.mutate(file);
                    event.target.value = "";
                  }}
                />
              </label>
            ) : null}
            {upload.isPending ? <p role="status">{t("scope.journals_uploading")}</p> : null}
            {uploadError !== null ? (
              <Banner tone="block">{t("scope.journals_upload_failed", { reason: uploadError })}</Banner>
            ) : null}
            {report !== null && report.stored ? (
              <p data-testid="journals-on-file">
                {t("scope.journals_report_on_file", { filename: report.stored_filename ?? "" })}
                {report.journals !== null && report.journals !== undefined
                  ? ` ${t("scope.journals_report_checked", { count: report.journals })}`
                  : ""}
              </p>
            ) : null}
            {report !== null &&
            report.status !== "uploaded" &&
            report.status !== "applied" &&
            (report.message ?? "") !== "" ? (
              <Banner tone="block">{report.message ?? ""}</Banner>
            ) : null}
            {counted ? (
              <p data-testid="journals-outcome">
                {t("scope.journals_outcome", {
                  imported: report.imported ?? 0,
                  covered: report.covered ?? 0,
                  review: report.needs_review ?? 0,
                })}
              </p>
            ) : report !== null && report.stored ? (
              <p>{t("scope.journals_outcome_pending")}</p>
            ) : null}
            {findings.length > 0
              ? findings.map((finding) => (
                  <Banner key={finding} tone="warn">
                    {finding}
                  </Banner>
                ))
              : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** `YYYY-MM-DD` of the UTC calendar day: the server checks the report's period against ITS "today", which is UTC
 * (`datetime.now(UTC).date()`), so the card names that day - a user behind UTC must be told the range the server will
 * actually require, not their own local date. */
function utcIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}
